import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DeviceDialog } from './DeviceDialog';
import { DeviceLogDialog } from './DeviceLogDialog';
import { toast } from 'sonner';
import {
  Shield,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Plus,
  Edit2,
  Trash2,
  Clock,
  MapPin,
  Network,
  Calendar,
  ScrollText,
} from 'lucide-react';
import {
  GET_ALL_DEVICES,
  CREATE_DEVICE_MUTATION,
  UPDATE_DEVICE_MUTATION,
  APPROVE_DEVICE_MUTATION,
  REJECT_DEVICE_MUTATION,
  BLOCK_DEVICE_MUTATION,
  DELETE_DEVICE_MUTATION,
} from '../../graphql/queries';
import { usePermission } from '../../hooks/usePermission';

interface TrustedDevice {
  id: string;
  deviceName: string;
  location: string;
  token: string;
  status: 'TOKEN_GENERATED' | 'REGISTERED' | 'APPROVED' | 'REJECTED' | 'BLOCKED';
  
  // Auto-collected data (filled when device registers with token)
  fingerprint?: string;
  ipAddress?: string;
  macAddress?: string;
  serialNumber?: string;
  userAgent?: string;
  screenResolution?: string;
  timezone?: string;
  lastSeen?: string;
  
  // Admin settings
  notes?: string;
  allowedIpRanges: string[];
  workingHours?: string;
  allowedDays: string[];
  approvedBy?: string;
  approvedAt?: string;
  registeredAt?: string;
  createdAt: string;
}

interface DeviceManagementProps {
  currentUserRole: string;
}

const DeviceManagement: React.FC<DeviceManagementProps> = ({ currentUserRole }) => {
  const canManage = usePermission('devices.manage');
  const canView = usePermission('devices.view');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<TrustedDevice | null>(null);
  const [logDialogDevice, setLogDialogDevice] = useState<TrustedDevice | null>(null);

  // GraphQL hooks
  const { data: devicesData, loading: devicesLoading, refetch: refetchDevices } = useQuery(GET_ALL_DEVICES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Devices loaded:", data.devices?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading devices:", error);
    },
  });
  const [createDevice] = useMutation(CREATE_DEVICE_MUTATION);
  const [updateDevice] = useMutation(UPDATE_DEVICE_MUTATION);
  const [approveDevice] = useMutation(APPROVE_DEVICE_MUTATION);
  const [rejectDevice] = useMutation(REJECT_DEVICE_MUTATION);
  const [blockDevice] = useMutation(BLOCK_DEVICE_MUTATION);
  const [deleteDevice] = useMutation(DELETE_DEVICE_MUTATION);

  const devices = devicesData?.devices || [];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APPROVED': return <ShieldCheck className="w-4 h-4 text-green-500" />;
      case 'REGISTERED': return <Shield className="w-4 h-4 text-blue-500" />;
      case 'TOKEN_GENERATED': return <Shield className="w-4 h-4 text-yellow-500" />;
      case 'REJECTED': return <ShieldX className="w-4 h-4 text-red-500" />;
      case 'BLOCKED': return <ShieldAlert className="w-4 h-4 text-red-600" />;
      default: return <Shield className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'bg-green-100 text-green-800';
      case 'REGISTERED': return 'bg-blue-100 text-blue-800';
      case 'TOKEN_GENERATED': return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'BLOCKED': return 'bg-red-200 text-red-900';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleSave = async (id: string | null, data: any) => {
    try {
      if (id) {
        // Update existing device
        await updateDevice({
          variables: {
            id,
            ...data
          }
        });
      } else {
        // Create new device
        await createDevice({
          variables: data
        });
      }

      await refetchDevices();
    } catch (error: any) {
      console.error('Error saving device:', error);

      // Extract user-friendly error message
      let userMessage = 'Wystąpił błąd podczas zapisywania urządzenia';

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        userMessage = graphQLError.message || userMessage;
      } else if (error.networkError) {
        userMessage = 'Błąd połączenia z serwerem. Sprawdź połączenie internetowe.';
      } else if (error.message) {
        userMessage = process.env.NODE_ENV === 'development' ? error.message : userMessage;
      }

      throw new Error(userMessage);
    }
  };

  const handleAddNew = () => {
    setSelectedDevice(null);
    setDialogOpen(true);
  };

  const handleEdit = (device: TrustedDevice) => {
    setSelectedDevice(device);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDevice(null);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć to urządzenie? Ta operacja jest nieodwracalna.')) {
      try {
        await deleteDevice({
          variables: { id }
        });
        await refetchDevices();
        toast.success('Urządzenie zostało pomyślnie usunięte');
      } catch (error: any) {
        console.error('Error deleting device:', error);
        const message = error.graphQLErrors?.[0]?.message || 'Wystąpił błąd podczas usuwania urządzenia';
        toast.error(message);
      }
    }
  };

  const handleStatusChange = async (device: TrustedDevice, newStatus: 'APPROVED' | 'REJECTED' | 'BLOCKED') => {
    try {
      // Use current user from props (should be passed from parent component)
      if (!canManage) {
        toast.error('Brak uprawnień do zmiany statusu urządzenia');
        return;
      }

      if (newStatus === 'APPROVED') {
        await approveDevice({
          variables: {
            deviceId: device.id
          }
        });
        toast.success('Urządzenie zostało zatwierdzone');
      } else if (newStatus === 'REJECTED') {
        await rejectDevice({
          variables: {
            deviceId: device.id,
            reason: 'Odrzucone przez administratora'
          }
        });
        toast.success('Urządzenie zostało odrzucone');
      } else if (newStatus === 'BLOCKED') {
        await blockDevice({
          variables: {
            deviceId: device.id,
            reason: 'Zablokowane przez administratora'
          }
        });
        toast.success('Urządzenie zostało zablokowane');
      }

      await refetchDevices();
    } catch (error: any) {
      console.error('Error changing device status:', error);
      const message = error.graphQLErrors?.[0]?.message || 'Wystąpił błąd podczas zmiany statusu urządzenia';
      toast.error(message);
    }
  };

  const copyTokenToClipboard = (token: string) => {
    navigator.clipboard.writeText(token).then(() => {
      toast.success('Token skopiowany do schowka!');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = token;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Token skopiowany do schowka!');
    });
  };

  if (!canView) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Brak uprawnień do zarządzania urządzeniami
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Zarządzanie urządzeniami</h2>
          <p className="text-muted-foreground">Zarządzaj zaufanymi urządzeniami w systemie</p>
        </div>
        <Button onClick={handleAddNew} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Dodaj urządzenie
        </Button>
      </div>

      {/* Statystyki */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-yellow-500" />
              <div>
                <p className="text-sm text-muted-foreground">Tokeny</p>
                <p className="text-2xl font-bold">{devices.filter((d: TrustedDevice) => d.status === 'TOKEN_GENERATED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              <div>
                <p className="text-sm text-muted-foreground">Zarejestrowane</p>
                <p className="text-2xl font-bold">{devices.filter((d: TrustedDevice) => d.status === 'REGISTERED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-green-500" />
              <div>
                <p className="text-sm text-muted-foreground">Zatwierdzone</p>
                <p className="text-2xl font-bold">{devices.filter((d: TrustedDevice) => d.status === 'APPROVED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ShieldX className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm text-muted-foreground">Odrzucone</p>
                <p className="text-2xl font-bold">{devices.filter((d: TrustedDevice) => d.status === 'REJECTED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-red-600" />
              <div>
                <p className="text-sm text-muted-foreground">Zablokowane</p>
                <p className="text-2xl font-bold">{devices.filter((d: TrustedDevice) => d.status === 'BLOCKED').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista urządzeń */}
      <Card>
        <CardHeader>
          <CardTitle>Lista urządzeń</CardTitle>
        </CardHeader>
        <CardContent>
          {devicesLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-muted-foreground">Ładowanie urządzeń...</p>
            </div>
          ) : devices.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Brak urządzeń w systemie. Dodaj pierwsze urządzenie.
            </div>
          ) : (
            <div className="space-y-4">
              {devices.map((device: TrustedDevice) => (
                <div key={device.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(device.status)}
                        <h3 className="font-semibold">{device.deviceName}</h3>
                        <Badge className={getStatusColor(device.status)}>
                          {device.status === 'TOKEN_GENERATED' ? 'Oczekuje na rejestrację' :
                           device.status === 'REGISTERED' ? 'Zarejestrowane' :
                           device.status === 'APPROVED' ? 'Zatwierdzone' :
                           device.status === 'REJECTED' ? 'Odrzucone' :
                           device.status === 'BLOCKED' ? 'Zablokowane' : device.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {device.status === 'TOKEN_GENERATED' && (
                          <>
                            <strong>Token:</strong> 
                            <span className="font-mono bg-yellow-100 px-2 py-1 rounded ml-1 mr-2">
                              {device.token}
                            </span>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => copyTokenToClipboard(device.token)}
                              className="text-xs h-6 px-2"
                            >
                              Kopiuj
                            </Button>
                          </>
                        )}
                        {device.fingerprint && (
                          <>ID: {device.fingerprint}</>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {device.status === 'REGISTERED' && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleStatusChange(device, 'APPROVED')}
                            className="text-green-600 hover:text-green-700"
                          >
                            Zatwierdź
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleStatusChange(device, 'REJECTED')}
                            className="text-red-600 hover:text-red-700"
                          >
                            Odrzuć
                          </Button>
                        </>
                      )}
                      {device.status === 'APPROVED' && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleStatusChange(device, 'BLOCKED')}
                          className="text-red-600 hover:text-red-700"
                        >
                          Zablokuj
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setLogDialogDevice(device)}
                        title="Logi urządzenia"
                      >
                        <ScrollText className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(device)}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(device.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-muted-foreground" />
                      <span>{device.location}</span>
                    </div>
                    {device.macAddress && (
                      <div className="flex items-center gap-2">
                        <Network className="w-4 h-4 text-muted-foreground" />
                        <span>{device.macAddress}</span>
                      </div>
                    )}
                    {device.workingHours && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground" />
                        <span>{device.workingHours}</span>
                      </div>
                    )}
                    {device.allowedDays.length > 0 && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span>{device.allowedDays.join(', ')}</span>
                      </div>
                    )}
                  </div>

                  {/* Auto-collected technical data */}
                  {(device.userAgent || device.screenResolution || device.timezone || device.ipAddress) && (
                    <div className="border-t pt-3">
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">Dane techniczne (automatycznie zebrane):</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
                        {device.userAgent && (
                          <div><strong>User Agent:</strong> {device.userAgent.substring(0, 50)}...</div>
                        )}
                        {device.screenResolution && (
                          <div><strong>Rozdzielczość:</strong> {device.screenResolution}</div>
                        )}
                        {device.timezone && (
                          <div><strong>Strefa czasowa:</strong> {device.timezone}</div>
                        )}
                        {device.ipAddress && (
                          <div><strong>Adres IP:</strong> {device.ipAddress}</div>
                        )}
                        {device.serialNumber && (
                          <div><strong>Nr seryjny:</strong> {device.serialNumber}</div>
                        )}
                      </div>
                    </div>
                  )}

                  {device.notes && (
                    <div className="text-sm text-muted-foreground">
                      <strong>Notatki:</strong> {device.notes}
                    </div>
                  )}

                  <div className="text-xs text-muted-foreground">
                    Utworzono: {new Date(device.createdAt).toLocaleString('pl-PL')}
                    {device.registeredAt && (
                      <span> • Zarejestrowano: {new Date(device.registeredAt).toLocaleString('pl-PL')}</span>
                    )}
                    {device.approvedAt && (
                      <span> • Zatwierdzono: {new Date(device.approvedAt).toLocaleString('pl-PL')}</span>
                    )}
                    {device.lastSeen && (
                      <span> • Ostatnio widziano: {new Date(device.lastSeen).toLocaleString('pl-PL')}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <DeviceDialog
        device={selectedDevice}
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSave}
      />

      <DeviceLogDialog
        device={logDialogDevice}
        open={!!logDialogDevice}
        onClose={() => setLogDialogDevice(null)}
      />
    </div>
  );
};

export default DeviceManagement;